import WebApp from '@twa-dev/sdk';

export function initTelegram() {
  // Dev-only convenience: ?dev_init_data=<signed> in the URL seeds localStorage so a plain
  // browser (no Telegram) can be authenticated with one click. No-ops in production builds.
  if (import.meta.env.DEV) {
    const url = new URL(window.location.href);
    const devInitData = url.searchParams.get('dev_init_data');
    if (devInitData) {
      localStorage.setItem('__dev_init_data', devInitData);
      url.searchParams.delete('dev_init_data');
      window.history.replaceState({}, '', url.toString());
    }
  }
  try {
    WebApp.ready();
    WebApp.expand();
    WebApp.setHeaderColor('#151B2E');
    WebApp.setBackgroundColor('#0A0E1A');
    WebApp.enableClosingConfirmation();
  } catch {
    // running outside Telegram (plain browser preview) — no-op
  }
}

export function getInitDataRaw(): string {
  if (WebApp.initData) return WebApp.initData;
  // Dev-only fallback so the app is testable in a plain browser preview outside Telegram.
  // Set via: localStorage.setItem('__dev_init_data', '<signed initData>'). No-ops in production builds.
  if (import.meta.env.DEV) {
    return localStorage.getItem('__dev_init_data') ?? '';
  }
  return '';
}

export function isInTelegram(): boolean {
  return Boolean(WebApp.initData);
}

type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';

export function hapticImpact(style: HapticStyle = 'light') {
  try {
    WebApp.HapticFeedback.impactOccurred(style);
  } catch {
    /* no-op outside Telegram */
  }
}

export function hapticNotification(type: 'error' | 'success' | 'warning') {
  try {
    WebApp.HapticFeedback.notificationOccurred(type);
  } catch {
    /* no-op outside Telegram */
  }
}

export function hapticSelection() {
  try {
    WebApp.HapticFeedback.selectionChanged();
  } catch {
    /* no-op outside Telegram */
  }
}

export function setMainButton(opts: {
  text: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  color?: `#${string}`;
}) {
  try {
    WebApp.MainButton.setText(opts.text);
    WebApp.MainButton.offClick(opts.onClick);
    WebApp.MainButton.onClick(opts.onClick);
    if (opts.color) WebApp.MainButton.color = opts.color;
    // showProgress() alone does NOT make the button unclickable — a loading button must also be
    // disabled, otherwise a double-tap (or slow network) fires onClick again mid-request.
    if (opts.disabled || opts.loading) WebApp.MainButton.disable();
    else WebApp.MainButton.enable();
    if (opts.loading) WebApp.MainButton.showProgress(false);
    else WebApp.MainButton.hideProgress();
    WebApp.MainButton.show();
  } catch {
    /* no-op outside Telegram */
  }
}

export function hideMainButton() {
  try {
    WebApp.MainButton.hide();
  } catch {
    /* no-op outside Telegram */
  }
}

export function setBackButton(onClick: () => void) {
  try {
    WebApp.BackButton.offClick(onClick);
    WebApp.BackButton.onClick(onClick);
    WebApp.BackButton.show();
  } catch {
    /* no-op outside Telegram */
  }
}

export function hideBackButton() {
  try {
    WebApp.BackButton.hide();
  } catch {
    /* no-op outside Telegram */
  }
}

export { WebApp };
